import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth, useApiResponseParser, useNotification } from ".";
import { useOrganization } from "./query";

import type { IUserOrganization } from "../interfaces";

function normalizeOrganizations(response: unknown): IUserOrganization[] {
    if (Array.isArray(response)) return response;
    if (response && typeof response === "object") return [response as IUserOrganization];
    return [];
}

function isImpersonationOrg(org: IUserOrganization | null | undefined) {
    return !!org && (org.IsImpersonation === true || org.IsImpersonation === 1);
}

export default function useOrganizationImpersonation() {
    const queryClient = useQueryClient();
    const { parseErrorResponseData } = useApiResponseParser();
    const { showError, showSuccess } = useNotification();
    const { organizations, selectedOrganization, setOrganizations, setSelectedOrganization } = useAuth();
    const { useListByUser, useImpersonate, useStopImpersonation } = useOrganization();

    const listByUserSvc = useListByUser();
    const impersonateSvc = useImpersonate();
    const stopImpersonationSvc = useStopImpersonation();

    const invalidateOrgScopedQueries = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["projects"] }),
            queryClient.invalidateQueries({ queryKey: ["project-summary"] }),
            queryClient.invalidateQueries({ queryKey: ["integrations"] }),
            queryClient.invalidateQueries({ queryKey: ["roles"] }),
        ]);
    }, [queryClient]);

    const refreshOrganizations = useCallback(async () => {
        const response = await listByUserSvc.mutateAsync();
        const nextOrganizations = normalizeOrganizations(response);
        setOrganizations(nextOrganizations);
        return nextOrganizations;
    }, [listByUserSvc, setOrganizations]);

    const startImpersonation = useCallback(
        async (organizationId: string) => {
            try {
                const result = await impersonateSvc.mutateAsync({ OrganizationId: organizationId });
                const refreshed = await refreshOrganizations();
                const organization =
                    refreshed.find((org) => org.Id === organizationId) || result?.Organization || null;

                if (organization) {
                    setSelectedOrganization(organization);
                }

                await invalidateOrgScopedQueries();
                showSuccess(`Now viewing ${organization?.Name || "organization"} as admin`);
                return organization;
            } catch (error) {
                showError(parseErrorResponseData(error) || "Failed to access organization");
                throw error;
            }
        },
        [
            impersonateSvc,
            refreshOrganizations,
            setSelectedOrganization,
            invalidateOrgScopedQueries,
            showSuccess,
            showError,
            parseErrorResponseData,
        ],
    );

    const stopImpersonation = useCallback(
        async (organizationId?: string) => {
            const targetId = organizationId || selectedOrganization?.Id;
            if (!targetId) return;

            try {
                await stopImpersonationSvc.mutateAsync({ OrganizationId: targetId });
                const refreshed = await refreshOrganizations();
                const nextSelected =
                    refreshed.find((org) => org.Id !== targetId) || refreshed[0] || null;
                setSelectedOrganization(nextSelected);
                await invalidateOrgScopedQueries();
                showSuccess("Admin access ended");
            } catch (error) {
                showError(parseErrorResponseData(error) || "Failed to exit admin access");
                throw error;
            }
        },
        [
            selectedOrganization?.Id,
            stopImpersonationSvc,
            refreshOrganizations,
            setSelectedOrganization,
            invalidateOrgScopedQueries,
            showSuccess,
            showError,
            parseErrorResponseData,
        ],
    );

    return {
        organizations,
        selectedOrganization,
        isImpersonating: isImpersonationOrg(selectedOrganization),
        startImpersonation,
        stopImpersonation,
        isStarting: impersonateSvc.isPending || listByUserSvc.isPending,
        isStopping: stopImpersonationSvc.isPending || listByUserSvc.isPending,
    };
}

export { isImpersonationOrg };
